import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  Link,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { registerUser } from "../../API/API";
import { setUser } from "../../ReduxStore/UserSlice";
import { showToast } from "../../components/common/Util/Util";
import LenderOnboardingPopup from "../../components/forms/LenderOnboarding/LenderOnboardingPopup";
import OrganizationVerificationPopup from "../../components/forms/LenderOnboarding/OrganizationVerificationPopup";
import { openDashboardInNewTab } from "../../utils/navigationUtils";
import {
  USER_TYPES,
  BORROWER_TYPES,
  ENTITY_TYPES,
} from "../../utils/constants";
import ConsultantOnboardingDialog from "../../components/forms/ConsultantOnboarding/ConsultantOnboardingForm";

const WelcomeScreen = ({
  showWelcome,
  setShowWelcome,
  requestData,
  redirectToApplyLoan,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedRole, setSelectedRole] = useState("");
  const [selectionStep, setSelectionStep] = useState(
    requestData?.continueAs || "main"
  );
  const [borrowerType, setBorrowerType] = useState("");
  const [entityType, setEntityType] = useState("");
  const [entityName, setEntityName] = useState("");
  const [showLenderOnboarding, setShowLenderOnboarding] = useState(false);
  const [showOrgVerification, setShowOrgVerification] = useState(false);
  const [lenderId, setLenderId] = useState(null);

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    if (event.target.value === "borrower") {
      setSelectionStep("borrowerType");
    } else if (event.target.value === "agent") {
      setSelectionStep("main");
    } else if (event.target.value === "lender") {
      setShowLenderOnboarding(true);
    }
  };

  useEffect(() => {
    if (requestData?.continueAs === "borrowerType") {
      setSelectionStep("borrowerType");
      selectedRole("borrower");
    } else if (requestData?.continueAs === "lenderType") {
      setShowLenderOnboarding(true);
      selectedRole("lender");
    } else if (requestData?.continueAs === "counsultantType") {
      setShowLenderOnboarding(true);
      selectedRole("agent");
    } else {
      setSelectionStep("main");
      selectedRole("");
    }
  }, [requestData?.continueAs]);

  const handleBorrowerTypeChange = (event) => {
    setBorrowerType(event.target.value);
    if (event.target.value === "entity") {
      setSelectionStep("entityType");
    }
  };

  const handleEntityTypeChange = (event) => {
    setEntityType(event.target.value);
  };

  const handleClose = () => {
    setShowWelcome(false);
  };

  const handleLenderOnboardingBack = () => {
    setShowLenderOnboarding(false);
    setSelectedRole("");
    handleClose();
    // setSelectionStep("main");
  };

  const handleCompleteClose = () => {
    setShowLenderOnboarding(false);
    setSelectedRole("");
    // setSelectionStep("main");
    handleClose();
  };

  const handleOrgVerificationClose = () => {
    setShowOrgVerification(false);
    setSelectedRole("");
    setSelectionStep("main");
    handleClose();
  };

  const handleRegister = () => {
    const cleanRequestData = {
      ...requestData,
    };

    if (selectedRole === "agent") {
      cleanRequestData.userType = "agent";
    } else if (selectedRole === "borrower") {
      cleanRequestData.userType = USER_TYPES.BORROWER;
      cleanRequestData.borrowerType = borrowerType;
      if (borrowerType === BORROWER_TYPES.ENTITY) {
        let entityTypeValue = "";
        if (entityType === "business") {
          entityTypeValue = ENTITY_TYPES.COMPANY;
        } else if (entityType === "partnership") {
          entityTypeValue = ENTITY_TYPES.PARTNERSHIP_FIRM;
        } else if (entityType === "proprietorship") {
          entityTypeValue = ENTITY_TYPES.propreitorship_firm;
        }

        cleanRequestData.entityType = entityTypeValue;
        cleanRequestData.entityName = entityName;
      }
    }

    registerUser(cleanRequestData)
      .then((res) => {
        if (res?.statusCode === 200) {
          showToast(res.message, "success");

          if (res.result) {
            if (borrowerType === BORROWER_TYPES.ENTITY && entityType) {
              res.result.entityType = cleanRequestData.entityType;
              res.result.borrowerType = cleanRequestData.borrowerType;
              res.result.entityName = cleanRequestData.entityName;

              const userData = { ...res.result };
              localStorage.setItem("user", JSON.stringify(userData));
            }
          }

          dispatch(setUser(res.result));
          localStorage.setItem("jwtToken", res.result.jwtToken);
          handleClose();
          if (redirectToApplyLoan) {
            window.open("/apply-loan", "_blank");
          } else {
            openDashboardInNewTab();
          }
        }
      })
      .catch((e) => {
        showToast("Oops! Something went wrong.", "error");
        console.error(e);
      });
  };

  const handleBack = () => {
    if (selectionStep === "entityType") {
      setSelectionStep("borrowerType");
    } else if (
      selectionStep === "borrowerType" ||
      selectionStep === "lenderForm"
    ) {
      setSelectedRole("");
      handleClose();
    }
  };

  const shouldShowContinueButton = () => {
    if (
      selectionStep === "main" &&
      (selectedRole === "agent" || selectedRole === "lender")
    )
      return true;
    if (
      selectionStep === "borrowerType" &&
      borrowerType === BORROWER_TYPES.INDIVIDUAL
    )
      return true;
    if (selectionStep === "entityType" && entityType && entityName.trim())
      return true;
    return false;
  };

  return (
    <Dialog
      open={showWelcome}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "20px",
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent sx={{ padding: 0, position: "relative" }}>
        <CloseIcon
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            cursor: "pointer",
            zIndex: 2,
          }}
        />
        <Grid container sx={{ height: "100%" }}>
          {/* Left Side Image (Merged with Border) */}
          <Grid
            item
            xs={12}
            sm={5}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `url(fintech.png) center/cover no-repeat`,
            }}
          />

          {/* Right Side Form */}
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "2rem",
              position: "relative",
              [theme.breakpoints.down("sm")]: { padding: "1rem" },
            }}
          >
            {/* Logo at Top Center */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src="/dequiLogo.png"
                alt="Logo"
                style={{ width: "150px" }}
              />
            </Box>

            {selectionStep === "main" && (
              <>
                <Typography
                  sx={{ fontWeight: 700, color: "#004AAD", fontSize: "18px" }}
                >
                  Welcome to Dequitec!
                </Typography>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    fontWeight: "bold",
                    marginBottom: "0.3rem",
                    fontSize: "14px",
                  }}
                >
                  Let's get started by identifying your role.
                </Typography>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    marginBottom: "1rem",
                    fontSize: "12px",
                  }}
                >
                  Which type of user are you?
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginBottom: "1rem",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid #818181",
                      borderRadius: "8px",
                      padding: "1rem",
                      flex: 1,
                      cursor: "pointer",
                      backgroundColor:
                        selectedRole === "borrower"
                          ? "rgba(0, 74, 173, 0.1)"
                          : "transparent",
                      borderColor:
                        selectedRole === "borrower" ? "#004AAD" : "#818181",
                    }}
                    onClick={() =>
                      handleRoleChange({ target: { value: "borrower" } })
                    }
                  >
                    <FormControlLabel
                      value="borrower"
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={selectedRole === "borrower"}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#333333",
                            }}
                          >
                            1. Borrower
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#818181" }}
                          >
                            Looking to avail financial assistance.
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    />
                  </Box>

                  <>
                    <Box
                      sx={{
                        border: "1px solid #818181",
                        borderRadius: "8px",
                        padding: "1rem",
                        flex: 1,
                        cursor: "pointer",
                        backgroundColor:
                          selectedRole === "lender"
                            ? "rgba(0, 74, 173, 0.1)"
                            : "transparent",
                        borderColor:
                          selectedRole === "lender" ? "#004AAD" : "#818181",
                      }}
                      onClick={() =>
                        handleRoleChange({ target: { value: "lender" } })
                      }
                    >
                      <FormControlLabel
                        value="lender"
                        control={
                          <Radio
                            style={{ color: "#004AAD" }}
                            checked={selectedRole === "lender"}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.8rem",
                                color: "#333333",
                              }}
                            >
                              1. Lender
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.7rem", color: "#818181" }}
                            >
                              An organization or person that lends money.
                            </Typography>
                          </Box>
                        }
                        sx={{ display: "flex", alignItems: "flex-start" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        border: "1px solid #818181",
                        borderRadius: "8px",
                        padding: "1rem",
                        flex: 1,
                        cursor: "pointer",
                        backgroundColor:
                          selectedRole === "agent"
                            ? "rgba(0, 74, 173, 0.1)"
                            : "transparent",
                        borderColor:
                          selectedRole === "agent" ? "#004AAD" : "#818181",
                      }}
                      onClick={() =>
                        handleRoleChange({ target: { value: "agent" } })
                      }
                    >
                      <FormControlLabel
                        value="agent"
                        control={
                          <Radio
                            style={{ color: "#004AAD" }}
                            checked={selectedRole === "agent"}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.8rem",
                                color: "#333333",
                              }}
                            >
                              2. Consultant
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.7rem", color: "#818181" }}
                            >
                              Act as an intermediary, connecting borrowers with
                              lenders to facilitate the loan process.
                            </Typography>
                          </Box>
                        }
                        sx={{ display: "flex", alignItems: "flex-start" }}
                      />
                    </Box>
                  </>
                </Box>
              </>
            )}

            {selectionStep === "borrowerType" && (
              <>
                <Typography
                  sx={{ fontWeight: 700, color: "#004AAD", fontSize: "18px" }}
                >
                  Type of Borrower
                </Typography>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    marginBottom: "1rem",
                    fontSize: "12px",
                  }}
                >
                  Please select the type of borrower you are:
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginBottom: "1rem",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid #818181",
                      borderRadius: "8px",
                      padding: "1rem",
                      flex: 1,
                      cursor: "pointer",
                      backgroundColor:
                        borrowerType === BORROWER_TYPES.INDIVIDUAL
                          ? "rgba(0, 74, 173, 0.1)"
                          : "transparent",
                      borderColor:
                        borrowerType === BORROWER_TYPES.INDIVIDUAL
                          ? "#004AAD"
                          : "#818181",
                    }}
                    onClick={() =>
                      handleBorrowerTypeChange({
                        target: { value: BORROWER_TYPES.INDIVIDUAL },
                      })
                    }
                  >
                    <FormControlLabel
                      value={BORROWER_TYPES.INDIVIDUAL}
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={borrowerType === BORROWER_TYPES.INDIVIDUAL}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#333333",
                            }}
                          >
                            1. Individual
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#818181" }}
                          >
                            An individual seeking a personal or small business
                            loan
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #818181",
                      borderRadius: "8px",
                      padding: "1rem",
                      flex: 1,
                      cursor: "pointer",
                      backgroundColor:
                        borrowerType === BORROWER_TYPES.ENTITY
                          ? "rgba(0, 74, 173, 0.1)"
                          : "transparent",
                      borderColor:
                        borrowerType === BORROWER_TYPES.ENTITY
                          ? "#004AAD"
                          : "#818181",
                    }}
                    onClick={() =>
                      handleBorrowerTypeChange({
                        target: { value: BORROWER_TYPES.ENTITY },
                      })
                    }
                  >
                    <FormControlLabel
                      value={BORROWER_TYPES.ENTITY}
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={borrowerType === BORROWER_TYPES.ENTITY}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#333333",
                            }}
                          >
                            2. Entity
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#818181" }}
                          >
                            Represent a business entity applying for a loan
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {selectionStep === "entityType" && (
              <>
                <Typography
                  sx={{ fontWeight: 700, color: "#004AAD", fontSize: "18px" }}
                >
                  Which type of Entity
                </Typography>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    marginBottom: "1rem",
                    fontSize: "12px",
                  }}
                >
                  Please specify your entity type:
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    marginBottom: "1rem",
                  }}
                >
                  <TextField
                    fullWidth
                    label="Entity Name"
                    value={entityName}
                    onChange={(e) => setEntityName(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />

                  <Box
                    sx={{
                      border: "1px solid #818181",
                      borderRadius: "8px",
                      padding: "1rem",
                      cursor: "pointer",
                      backgroundColor:
                        entityType === "business"
                          ? "rgba(0, 74, 173, 0.1)"
                          : "transparent",
                      borderColor:
                        entityType === "business" ? "#004AAD" : "#818181",
                    }}
                    onClick={() =>
                      handleEntityTypeChange({ target: { value: "business" } })
                    }
                  >
                    <FormControlLabel
                      value="business"
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={entityType === "business"}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#333333",
                            }}
                          >
                            Company
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#818181" }}
                          >
                            A formal business organization
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #818181",
                      borderRadius: "8px",
                      padding: "1rem",
                      cursor: "pointer",
                      backgroundColor:
                        entityType === "partnership"
                          ? "rgba(0, 74, 173, 0.1)"
                          : "transparent",
                      borderColor:
                        entityType === "partnership" ? "#004AAD" : "#818181",
                    }}
                    onClick={() =>
                      handleEntityTypeChange({
                        target: { value: "partnership" },
                      })
                    }
                  >
                    <FormControlLabel
                      value="partnership"
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={entityType === "partnership"}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#333333",
                            }}
                          >
                            Partnership
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#818181" }}
                          >
                            A business partnership between two or more people
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #818181",
                      borderRadius: "8px",
                      padding: "1rem",
                      cursor: "pointer",
                      backgroundColor:
                        entityType === "proprietorship"
                          ? "rgba(0, 74, 173, 0.1)"
                          : "transparent",
                      borderColor:
                        entityType === "proprietorship" ? "#004AAD" : "#818181",
                    }}
                    onClick={() =>
                      handleEntityTypeChange({
                        target: { value: "proprietorship" },
                      })
                    }
                  >
                    <FormControlLabel
                      value="proprietorship"
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={entityType === "proprietorship"}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#333333",
                            }}
                          >
                            Proprietorship
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#818181" }}
                          >
                            A business owned and run by a single individual
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {/* Back and Continue Buttons */}
            {selectionStep !== "main" && (
              <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: "#003A91",
                    color: "#003A91",
                    height: "40px",
                    textTransform: "none",
                  }}
                  onClick={handleBack}
                >
                  Back
                </Button>

                {shouldShowContinueButton() && (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#003A91",
                      "&:hover": { backgroundColor: "#002570" },
                      height: "40px",
                      textTransform: "none",
                    }}
                    onClick={handleRegister}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            )}

            {/* Support Text */}
            <Typography
              sx={{
                textAlign: "center",
                marginTop: "1rem",
                fontSize: "0.8rem",
              }}
            >
              Not sure about your role? Contact our support team for assistance.
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                marginTop: "1rem",
                fontSize: "0.8rem",
              }}
            >
              Support Email:{" "}
              <Link
                href="contact@dequitec.com"
                sx={{ color: "#003A91", fontWeight: "bold" }}
              >
                contact@dequitec.com
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Lender Onboarding Popup */}
      <LenderOnboardingPopup
        open={showLenderOnboarding}
        onClose={handleCompleteClose}
        onBack={handleLenderOnboardingBack}
        initialData={requestData}
      />

      <ConsultantOnboardingDialog
        open={selectedRole === "agent"}
        onClose={handleClose}
        initialData={requestData}
        onBack={handleLenderOnboardingBack}
        onComplete={() => {
          setShowWelcome(false);
          openDashboardInNewTab();
        }}
      />

      {/* Organization Verification Popup */}
      <OrganizationVerificationPopup
        open={showOrgVerification}
        onClose={handleOrgVerificationClose}
        formData={requestData}
        initialData={requestData}
        lenderId={lenderId}
        preventClose={false}
      />
    </Dialog>
  );
};

export default WelcomeScreen;
